// src/features/system/devices/components/iot/IoTManagement.tsx
// ────────────────────────────────────────────────────────────────────────────
// Sub-tab container for ESP32 IoT device management.
// Pattern mirrors OrchestratorManagement.tsx — section tabs + panel render.
// ────────────────────────────────────────────────────────────────────────────
import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Tabs from '@/shared/components/ui/Tabs';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

import IoTDashboardPanel      from './IoTDashboardPanel';
import IoTDeviceRegistryPanel from './IoTDeviceRegistryPanel';
import IoTTelemetryPanel      from './IoTTelemetryPanel';
import IoTDeviceConfigPanel   from './IoTDeviceConfigPanel';
import IoTFirmwarePanel       from './IoTFirmwarePanel';
import IoTQuarantinePanel     from './IoTQuarantinePanel';
import IoTProvisioningPanel   from './IoTProvisioningPanel';
import IoTSharedLinksPanel    from './IoTSharedLinksPanel';
import IoTDeviceLogPanel      from './IoTDeviceLogPanel';
import IoTValidationPanel     from './IoTValidationPanel';
import DeviceApproverPanel    from '@/features/system/orchestrator/components/DeviceApproverPanel';

// ── Section definitions ──────────────────────────────────────────────────

type Section =
  | 'dashboard'
  | 'registry'
  | 'approvals'
  | 'telemetry'
  | 'logs'
  | 'config'
  | 'quarantine'
  | 'details'
  | 'provisioning'
  | 'validation'
  | 'shared-links';

const SECTION_TABS: Array<{ key: Section; label: string }> = [
  { key: 'dashboard',    label: 'Dashboard' },
  { key: 'registry',     label: 'Device Registry' },
  { key: 'approvals',    label: 'Device Approvals' },
  { key: 'telemetry',    label: 'Telemetry' },
  { key: 'logs',         label: 'Device Logs' },
  { key: 'config',       label: 'Configuration' },
  { key: 'quarantine',   label: 'Quarantine' },
  { key: 'details',      label: 'Details' },
  { key: 'provisioning', label: 'Provisioning' },
  { key: 'validation',   label: 'Validation' },
  { key: 'shared-links', label: 'Shared Links' },
];

const SECTION_COPY: Record<Section, { title: string; blurb: string }> = {
  dashboard: {
    title: 'IoT Dashboard',
    blurb: 'At-a-glance status of all registered devices — presence, telemetry snapshot, online/offline.',
  },
  registry: {
    title: 'Device Registry',
    blurb: 'Register, label, revoke, and blacklist devices. Each device is identified by its scope-based device_id.',
  },
  approvals: {
    title: 'Device Approvals',
    blurb: 'Unified approval queue \u2014 Tier 1 IoT sensors and Tier 2 smart clients (RFC 8628 device authorization).',
  },
  telemetry: {
    title: 'Telemetry',
    blurb: 'Hot-stream telemetry history for a selected device — temperature, humidity, RSSI, battery, and any custom metrics.',
  },
  logs: {
    title: 'Device Logs',
    blurb: 'Real-time device-side log stream ingested via the hot-stream pipeline — filterable by level and tag.',
  },
  config: {
    title: 'Configuration',
    blurb: 'View and push configuration updates to a device. Config is stored as a versioned key-value bag.',
  },
  quarantine: {
    title: 'Quarantine',
    blurb: 'Approve or reject pending devices. Manage the blacklist of explicitly rejected devices.',
  },
  details: {
    title: 'Device Details',
    blurb: 'Identity metadata, authorization status, platform, fingerprint, and approval info for every device.',
  },
  provisioning: {
    title: 'Provisioning Guide',
    blurb: 'Step-by-step instructions for onboarding a new ESP32-IoT sensor to the PACMaster platform.',
  },
  validation: {
    title: 'Device Validation',
    blurb: 'Run the 5-probe validation sequence (WiFi, health, sensor, telemetry, identity) and review baseline results.',
  },
  'shared-links': {
    title: 'Shared Links',
    blurb: 'Generate unique zero-trust URLs to share live IoT telemetry for selected devices with external viewers.',
  },
};

// ── Component ────────────────────────────────────────────────────────────

export default function IoTManagement() {
  const [section, setSection] = React.useState<Section>('dashboard');
  const [sp] = useSearchParams();
  const sectionLabel = SECTION_TABS.find(t => t.key === section)?.label ?? '';
  useDocumentTitle('Devices', 'IoT', sectionLabel);

  // Sync with URL when BubbleNav navigates while already mounted
  useEffect(() => {
    const v = sp.get('iotTab');
    if (!v || v === sectionLabel) return;
    const found = SECTION_TABS.find(t => t.label === v);
    if (found && found.key !== section) setSection(found.key);
  }, [sp]);

  const headerCopy = SECTION_COPY[section];

  return (
    <section className="h-full min-h-0 flex flex-col gap-4 overflow-hidden">
      <div className="space-y-3 shrink-0">
        <div className="max-w-full overflow-x-auto">
          <Tabs
            tabs={SECTION_TABS.map(t => t.label)}
            activeTab={SECTION_TABS.find(t => t.key === section)?.label ?? SECTION_TABS[0].label}
            urlKey="iotTab"
            urlParams={{ devicesTab: 'IoT' }}
            onTabChange={(label) => {
              const found = SECTION_TABS.find(t => t.label === label);
              if (found) setSection(found.key);
            }}
          />
        </div>
        {(headerCopy.title || headerCopy.blurb) && (
          <div>
            {headerCopy.title && (
              <h2 className="text-lg font-semibold tracking-tight">{headerCopy.title}</h2>
            )}
            {headerCopy.blurb && (
              <p className="text-xs text-neutral-500">{headerCopy.blurb}</p>
            )}
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        {section === 'dashboard'    && <IoTDashboardPanel />}
        {section === 'registry'     && <IoTDeviceRegistryPanel />}
        {section === 'approvals'    && <DeviceApproverPanel />}
        {section === 'telemetry'    && <IoTTelemetryPanel />}
        {section === 'logs'         && <IoTDeviceLogPanel />}
        {section === 'config'       && <IoTDeviceConfigPanel />}
        {section === 'quarantine'   && <IoTQuarantinePanel />}
        {section === 'details'      && <IoTFirmwarePanel />}
        {section === 'provisioning' && <IoTProvisioningPanel />}
        {section === 'validation'   && <IoTValidationPanel />}
        {section === 'shared-links' && <IoTSharedLinksPanel />}
      </div>
    </section>
  );
}
